import { useCallback } from 'react';

/**
 * Gets a getter and a setter function for handling a local-storage item.
 * @param key The key of the storage item.
 * @returns A getter and a setter function.
 */
export function useLocalStorage<T>(key: string) {
  const getValue = useCallback(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const parse = (value: string) => {
      try {
        return JSON.parse(value) as T;
      } catch {
        return undefined;
      }
    };

    const item = window.localStorage.getItem(key);
    return item ? parse(item) : undefined;
  }, [key]);

  const setValue = useCallback(
    (value: T | undefined) => {
      if (typeof window === 'undefined') {
        return;
      }

      if (typeof value !== 'undefined') {
        window.localStorage.setItem(key, JSON.stringify(value));
      } else {
        window.localStorage.removeItem(key);
      }
    },
    [key]
  );

  return { getValue, setValue };
}
